// React imports
import { FunctionComponent } from 'react';

// Third-party imports
import { faCopy, faBan, faPen } from '@fortawesome/pro-regular-svg-icons';

// Types
import { Promocode } from '@spike/promocode-model';

// UI Components
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown';

// Utils
import { formatPromoCodeDate, formatPromoCodeName } from '../Utils';

// Styles
import styles from './PromoCodesRow.module.scss';

interface PromoCodesRowProps {
    promoCode: Promocode;
    isLast: boolean;
    isMobile: boolean;
    onUsage?: (promoCode: Promocode) => void;
    onEdit?: (promoCode: Promocode) => void;
    onDeactivate?: (promoCode: Promocode) => void;
    onActivate?: (promoCode: Promocode) => void;
    isDropdownOpen?: boolean;
    onDropdownToggle?: (isOpen: boolean) => void;
}

const PromoCodesRow: FunctionComponent<PromoCodesRowProps> = ({
    promoCode,
    isLast,
    onEdit,
    onDeactivate,
    onActivate,
    onUsage,
    isDropdownOpen = false,
    onDropdownToggle
}) => {
    const editOption = {
        id: 'edit',
        label: 'Edit',
        icon: faPen,
        onClick: () => onEdit?.(promoCode)
    };

    const activateOption = {
        id: 'activate',
        label: 'Reactivate',
        icon: faCopy,
        onClick: () => onActivate?.(promoCode)
    };

    const deactivateOption = {
        id: 'deactivate',
        label: 'Deactivate',
        icon: faBan,
        onClick: () => onDeactivate?.(promoCode)
    };

    const options = (promoCode: Promocode) => {
        switch (promoCode.active ? 'Active' : 'Inactive') {
            case 'Active':
                return [editOption, deactivateOption];
            default:
                return [editOption, activateOption];
        }
    };

    const formattedName = formatPromoCodeName(promoCode);

    const startDateFormatted = formatPromoCodeDate(promoCode.startAt);
    const endDateFormatted = promoCode.endAt?.isValid ? formatPromoCodeDate(promoCode.endAt) : null;

    return (
        <TableRow className={styles.desktopRow}>
            <TableCell
                className={styles.nameMainCell}
                onClick={onUsage ? () => onUsage(promoCode) : undefined}
            >
                <div className={styles.serviceColor}></div>
                <div className={styles.nameCell}>
                    <p className={styles.promoName}>{formattedName.name}</p>
                    <p className={styles.promoSubtitle}>{formattedName.value}</p>
                </div>
            </TableCell>

            <TableCell onClick={onUsage ? () => onUsage(promoCode) : undefined}>
                <div className={styles.nameCell}>
                    <p className={styles.promoDate}>{startDateFormatted.date}</p>
                    <p className={styles.promoSubtitle}>{startDateFormatted.time}</p>
                </div>
            </TableCell>

            <TableCell onClick={onUsage ? () => onUsage(promoCode) : undefined}>
                <div className={styles.nameCell}>
                    {endDateFormatted ? (
                        <>
                            <p className={styles.promoDate}>{endDateFormatted.date}</p>
                            <p className={styles.promoSubtitle}>{endDateFormatted.time}</p>
                        </>
                    ) : (
                        <p>Never</p>
                    )}
                </div>
            </TableCell>

            <TableCell onClick={onUsage ? () => onUsage(promoCode) : undefined}>
                <p>{promoCode.totalRedeems}</p>
            </TableCell>
            <TableCell onClick={onUsage ? () => onUsage(promoCode) : undefined}>
                <p>{`$${promoCode.totalSales}`}</p>
            </TableCell>

            <TableCell
                isActions
                onClick={() => {
                    onDropdownToggle?.(!isDropdownOpen);
                }}
            >
                <ThreeDotsDropdown
                    options={options(promoCode)}
                    direction={isLast ? 'up' : 'down'}
                />
            </TableCell>
        </TableRow>
    );
};

export default PromoCodesRow;
